import {
	Component, Input, Output, OnInit, OnDestroy, OnChanges, SimpleChanges, SimpleChange,
	EventEmitter, ChangeDetectorRef, ViewChild, ElementRef, AfterViewInit
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { UsersService } from '../../services/users.service';
import { MessageService } from 'base';
import { AccountType, ActiveDirectorySettingsDto, ADAuthType, GetAuthSettingsResultDto, LoginResultCode, LoginResultDto }
	from 'UvExplorerServer.Dtos.TS/Auth';
import { List } from 'DiscoveryEngine.TS/Utils';
import { ObjSerUtils } from 'DiscoveryEngine.TS/Serialization';
import { IdentityService } from 'identity';
import { ComponentBase } from '../../../common';


@Component({
	selector: 'ad-auth-settings',
	templateUrl: './ad-auth-settings.component.html',
	styleUrls: ['./ad-auth-settings.component.scss']
})
export class AdAuthSettingsComponent extends ComponentBase implements OnInit, OnDestroy, OnChanges {

	public form: UntypedFormGroup;

	public adEnabled: UntypedFormControl;
	public adDomainNames: UntypedFormControl;
	public adAuthType: UntypedFormControl;
	public adServerHostIp: UntypedFormControl;
	public adServerPort: UntypedFormControl;
	public adUseSsl: UntypedFormControl;
	public adQueryPageSize: UntypedFormControl;
	public adAutoSyncDirectoryDataEnabled: UntypedFormControl;
	public adAutoSyncDirectoryDataDays: UntypedFormControl;
	public adAutoSyncDirectoryDataHours: UntypedFormControl;
	public adAutoSyncDirectoryDataMinutes: UntypedFormControl;
	public adAllowLdapReferrals: UntypedFormControl;

	private adSettingsLoadedSub: Subscription;
	private adSettings: ActiveDirectorySettingsDto;

	public adAdminDialogVisible: boolean;
	public adAdminDialogDomains: string[];

	constructor(private fb: UntypedFormBuilder,
		private usersService: UsersService,
		private identityService: IdentityService,
		private messageService: MessageService) {

		super();

		this.adSettings = null;

		this.resetAdAdminDialogState();

		this.adSettingsLoadedSub = this.usersService.onActiveDirectorySettingsLoaded.subscribe(
			(result: ActiveDirectorySettingsDto) => {
				this.copyAdSettings();
			});
	}

	private resetAdAdminDialogState(): void {
		this.adAdminDialogVisible = false;
		this.adAdminDialogDomains = [];
	}

	private copyAdSettings(): void {
		if (this.usersService.hasActiveDirectorySettings) {
			this.adSettings = new ActiveDirectorySettingsDto();
			ObjSerUtils.Copy(this.usersService.adSettings, this.adSettings);
		}
		else {
			this.adSettings = null;

			const errorMessage = "Could not retrieve authentication settings";
			this.messageService.displayErrorMessage(errorMessage);
		}

		this.buildForm();
	}

	public ngOnInit(): void {

		this.buildForm();

		if (this.usersService.hasActiveDirectorySettings) {
			this.copyAdSettings();
		}
	}

	public ngOnDestroy(): void {
		if (this.adSettingsLoadedSub) {
			this.adSettingsLoadedSub.unsubscribe();
			this.adSettingsLoadedSub = null;
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {

	}

	private buildForm(): void {
		let adEnabledValue = false;
		let adDomainNamesValue = "";
		let adAuthTypeValue = ADAuthType[ADAuthType.Windows];
		let adServerHostIpValue = "";
		let adServerPortValue = 0;
		let adUseSslValue = false;
		let adQueryPageSizeValue = 0;
		let adAutoSyncDirectoryDataEnabled = false;
		let adAutoSyncDirectoryDataDays = 0;
		let adAutoSyncDirectoryDataHours = 0;
		let adAutoSyncDirectoryDataMinutes = 0;
		let adAllowLdapReferrals = false;

		if (!!this.adSettings) {
			adEnabledValue = this.adSettings.Enabled;
			adDomainNamesValue = this.domainNamesToString(this.adSettings.DomainNames);
			adAuthTypeValue = ADAuthType[this.adSettings.AuthType];
			adServerHostIpValue = this.adSettings.ADServerHostIp;
			adServerPortValue = this.adSettings.ADServerPort;
			adUseSslValue = this.adSettings.UseSSL;
			adQueryPageSizeValue = this.adSettings.QueryPageSize;
			adAutoSyncDirectoryDataEnabled = this.adSettings.AutoSyncDirectoryData_Enabled;
			adAutoSyncDirectoryDataDays = this.adSettings.AutoSyncDirectoryData_Days;
			adAutoSyncDirectoryDataHours = this.adSettings.AutoSyncDirectoryData_Hours;
			adAutoSyncDirectoryDataMinutes = this.adSettings.AutoSyncDirectoryData_Minutes;
			adAllowLdapReferrals = this.adSettings.AllowLdapReferrals;
		}

		this.adEnabled = new UntypedFormControl(adEnabledValue);
		this.adDomainNames = new UntypedFormControl(adDomainNamesValue,
			this.requiredIf(() => !!this.adEnabled.value, "adDomainNamesRequired"));
		this.adAuthType = new UntypedFormControl(adAuthTypeValue);
		this.adServerHostIp = new UntypedFormControl(adServerHostIpValue,
			this.requiredIf(() => !!this.adEnabled.value &&
				this.adAuthType.value == ADAuthType[ADAuthType.LDAP], "adServerHostIpRequired"));
		this.adServerPort = new UntypedFormControl(adServerPortValue, Validators.compose([
			this.requiredIf(() => !!this.adEnabled.value &&
				this.adAuthType.value == ADAuthType[ADAuthType.LDAP], "adServerPortRequired"),
			this.isValidPort("adServerPortValid")]));
		this.adUseSsl = new UntypedFormControl(adUseSslValue);
		this.adQueryPageSize = new UntypedFormControl(adQueryPageSizeValue, Validators.compose([
			this.minValue(1), this.maxValue(1000)
		]));
		this.adAutoSyncDirectoryDataEnabled = new UntypedFormControl(adAutoSyncDirectoryDataEnabled);
		this.adAutoSyncDirectoryDataDays = new UntypedFormControl(adAutoSyncDirectoryDataDays,
			Validators.compose([Validators.required, this.minValue(0), this.maxValue(30)]));
		this.adAutoSyncDirectoryDataHours = new UntypedFormControl(adAutoSyncDirectoryDataHours,
			Validators.compose([Validators.required, this.minValue(0), this.maxValue(23)]));
		this.adAutoSyncDirectoryDataMinutes = new UntypedFormControl(adAutoSyncDirectoryDataMinutes,
			Validators.compose([Validators.required, this.minValue(0), this.maxValue(59)]));
		this.adAllowLdapReferrals = new UntypedFormControl(adAllowLdapReferrals);

		this.enableDisable();

		this.form = this.fb.group({
			adEnabled: this.adEnabled,
			adDomainNames: this.adDomainNames,
			adAuthType: this.adAuthType,
			adServerHostIp: this.adServerHostIp,
			adServerPort: this.adServerPort,
			adUseSsl: this.adUseSsl,
			adQueryPageSize: this.adQueryPageSize,
			adAutoSyncDirectoryDataEnabled: this.adAutoSyncDirectoryDataEnabled,
			adAutoSyncDirectoryDataDays: this.adAutoSyncDirectoryDataDays,
			adAutoSyncDirectoryDataHours: this.adAutoSyncDirectoryDataHours,
			adAutoSyncDirectoryDataMinutes: this.adAutoSyncDirectoryDataMinutes,
			adAllowLdapReferrals: this.adAllowLdapReferrals,
		},
		{
			validator: this.validAutoSyncSettings()
		});

		setTimeout(() => this.enableDisable(), 0);
	}

	public enableDisableControls(): void {
		this.enableDisableFormControl(this.adAutoSyncDirectoryDataDays, this.adAutoSyncDirectoryDataEnabled.value);
		this.enableDisableFormControl(this.adAutoSyncDirectoryDataHours, this.adAutoSyncDirectoryDataEnabled.value);
		this.enableDisableFormControl(this.adAutoSyncDirectoryDataMinutes, this.adAutoSyncDirectoryDataEnabled.value);
	}

	private validAutoSyncSettings(): ValidatorFn {
		return () => {
			if (!this.adAutoSyncDirectoryDataEnabled.value) {
				return null;
			}

			if (this.adAutoSyncDirectoryDataDays.value > 0 ||
				this.adAutoSyncDirectoryDataHours.value > 0 ||
				this.adAutoSyncDirectoryDataMinutes.value >= 15) {

				return null;
			}

			return {
				nonEmptyAutoSyncSettings: { valid: false }
			};
		};
	}

	private domainNamesToString(domainNames: List<string>) {
		let result = "";

		domainNames.ForEach((dn: string) => {
			result += (dn + '\n');
		});

		return result;
	}

	private stringToDomainNames(str: string): List<string> {
		let result = new List<string>();

		let domainNames = str.split(/\s+/);
		domainNames.forEach((v: string) => {
			if (v) {
				result.Add(v);
			}
		});

		return result;
	}

	public checkChanged(): void {
		this.enableDisable();
	}

	public useSslChanged(): void {

		if (this.adUseSsl.value) {
			if (this.adServerPort.value == ActiveDirectorySettingsDto.Default_ADServerPort_NoSsl) {
				this.adServerPort.setValue(ActiveDirectorySettingsDto.Default_ADServerPort_Ssl);
			}
		}
		else {
			if (this.adServerPort.value == ActiveDirectorySettingsDto.Default_ADServerPort_Ssl) {
				this.adServerPort.setValue(ActiveDirectorySettingsDto.Default_ADServerPort_NoSsl);
			}
		}

		this.enableDisable();
	}

	private enableDisable(): void {
		if (this.adEnabled.value) {
			this.adDomainNames.enable();
			this.adQueryPageSize.enable();
			this.adAllowLdapReferrals.enable();
			this.adAutoSyncDirectoryDataEnabled.enable();
			if (this.adAutoSyncDirectoryDataEnabled.value) {
				this.adAutoSyncDirectoryDataDays.enable();
				this.adAutoSyncDirectoryDataHours.enable();
				this.adAutoSyncDirectoryDataMinutes.enable();
			}
			else {
				this.adAutoSyncDirectoryDataDays.disable();
				this.adAutoSyncDirectoryDataHours.disable();
				this.adAutoSyncDirectoryDataMinutes.disable();
			}
			this.adAuthType.enable();
			if (this.adAuthType.value == ADAuthType[ADAuthType.LDAP]) {
				this.adServerHostIp.enable();
				this.adServerPort.enable();
				this.adUseSsl.enable();
			}
			else {
				this.adServerHostIp.disable();
				this.adServerPort.disable();
				this.adUseSsl.disable();
			}
		}
		else {
			this.adDomainNames.disable();
			this.adQueryPageSize.disable();
			this.adAllowLdapReferrals.disable();
			this.adAutoSyncDirectoryDataEnabled.disable();
			this.adAutoSyncDirectoryDataDays.disable();
			this.adAutoSyncDirectoryDataHours.disable();
			this.adAutoSyncDirectoryDataMinutes.disable();
			this.adAuthType.disable();
			this.adServerHostIp.disable();
			this.adServerPort.disable();
			this.adUseSsl.disable();
		}
	}

	public enableSave(): boolean {
		return (!!this.adSettings && this.form.dirty && this.form.valid);
	}

	public onAdAdminDialogOkClicked({ username: username, password: password, domain: domain }): void {

		this.getSettings();

		this.messageService.displayInfoMessage("Logging in to Active Directory");
		this.messageService.startWork();
		this.identityService.loginActiveDirectorySetAuthSettingsAdmin(this.adSettings,
			username, password, domain).subscribe(
				(result: LoginResultDto) => {
					this.messageService.endWork();
					if (result.Code === LoginResultCode.Succeeded) {
						this.save();
					}
					else {
						this.messageService.displayErrorMessage("Login failed");
					}
				},
				(error) => {
					this.messageService.endWork();
					this.messageService.displayErrorMessage("Login failed");
				}
			);

		this.resetAdAdminDialogState();
	}

	public onAdAdminDialogCancelClicked(): void {
		this.resetAdAdminDialogState();
	}

	public onSave(): void {
		if (this.adEnabled.value) {
			this.adAdminDialogDomains = this.stringToDomainNames(this.adDomainNames.value).ToArray();
			this.adAdminDialogVisible = true;
		}
		else {
			this.save();
		}
	}

	private save(): void {
		this.getSettings();

		this.messageService.displayInfoMessage("Retrieving Active Directory groups");
		this.messageService.startWork();
		this.usersService.setAuthSettings(this.adSettings, null, null).subscribe(
			(result: boolean) => {
				//this.usersService.dirGroupsCache.remove(AccountType.ActiveDirectory);
				this.buildForm();
				this.messageService.endWork();
				this.messageService.displayInfoMessage("Authentication settings were saved");
			},
			(error) => {
				//this.usersService.dirGroupsCache.remove(AccountType.ActiveDirectory);
				this.messageService.endWork();
				this.messageService.displayErrorMessage("Could not save authentication settings");
			});
	}

	private getSettings(): void {
		this.adSettings.Enabled = this.adEnabled.value;
		this.adSettings.DomainNames = this.stringToDomainNames(this.adDomainNames.value);
		this.adSettings.AuthType = ADAuthType[<string>this.adAuthType.value];
		this.adSettings.ADServerHostIp = this.adServerHostIp.value;
		this.adSettings.ADServerPort = this.adServerPort.value;
		this.adSettings.UseSSL = this.adUseSsl.value;
		this.adSettings.QueryPageSize = this.adQueryPageSize.value;
		this.adSettings.AutoSyncDirectoryData_Enabled = this.adAutoSyncDirectoryDataEnabled.value;
		this.adSettings.AutoSyncDirectoryData_Days = this.adAutoSyncDirectoryDataDays.value;
		this.adSettings.AutoSyncDirectoryData_Hours = this.adAutoSyncDirectoryDataHours.value;
		this.adSettings.AutoSyncDirectoryData_Minutes = this.adAutoSyncDirectoryDataMinutes.value;
		this.adSettings.AllowLdapReferrals = this.adAllowLdapReferrals.value;
	}

}
